import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { Qty } from '../../../components/Money';
import { money } from '../../../lib/format';
import { errorMessage } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';
import { useCustomerAccount } from '../components/CustomerAccount';
import { currencySymbol, shopToday, usePaymentMethods, type SalesOrder } from '../lib/api';
import { dec, isDecimal, max, mulDivRound, sum, toFixed, toPlain } from '../lib/decimal';
import { qtyRowsProblems, qtyRowsRequest, type QtyRow } from '../lib/documents';

export interface QtySubmit {
  docDate: string;
  /** Due date (invoice only; empty = customer terms). */
  dueDate: string;
  lines: { salesOrderLineId: string; qty: string }[];
  /** Money received as the invoice is posted (invoice only). */
  payment?: { amountReceived: string; methodId: string };
}

/**
 * Deliver or invoice an order: one "now" field per line (default: everything left), with a quick
 * "all remaining" / "none" toggle. Over-delivery and over-invoicing are blocked before the server does.
 */
export function OrderQtyDialog({
  open,
  order,
  mode,
  onClose,
  onSubmit,
}: {
  open: boolean;
  order: SalesOrder;
  mode: 'deliver' | 'invoice';
  onClose: () => void;
  onSubmit: (input: QtySubmit) => Promise<unknown>;
}) {
  const { format } = useShop();
  const remainingOf = (l: SalesOrder['lines'][number]) =>
    toPlain(dec(mode === 'deliver' ? l.qtyToDeliver : l.qtyToInvoice));
  const open_ = order.lines.filter((l) => dec(remainingOf(l)) > 0n);
  const [rows, setRows] = useState<QtyRow[]>(() =>
    open_.map((l) => ({ lineId: l.id, remaining: remainingOf(l), qty: remainingOf(l) })),
  );
  const [docDate, setDocDate] = useState(shopToday(format.timeZone));
  const [dueDate, setDueDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const problems = qtyRowsProblems(rows, mode);
  const allSet = rows.every((r) => r.qty === r.remaining);

  // Payment with the invoice: a walk-in customer pays in full, anyone else may pay now.
  const invoicing = mode === 'invoice';
  const account = useCustomerAccount(invoicing ? order.contact.id : null);
  const walkIn = account?.isWalkIn ?? false;
  const methods = usePaymentMethods();
  const [paying, setPaying] = useState(false);
  const takePayment = invoicing && (walkIn || paying);
  const cash = methods.data?.find((m) => m.kind === 'CASH') ?? methods.data?.[0];
  const [methodId, setMethodId] = useState<string | null>(null);
  const method = methods.data?.find((m) => m.id === methodId) ?? cash;
  // What this invoice comes to: each line's total in proportion to the quantity invoiced now.
  const estimate = sum(
    open_.map((l, i) => {
      const now = rows[i]?.qty ?? '';
      return isDecimal(now) && dec(l.qty) > 0n
        ? mulDivRound(dec(l.lineTotal), dec(now), dec(l.qty), format.currencyDecimals)
        : 0n;
    }),
  );
  const [amount, setAmount] = useState<string | null>(null);
  const received = amount ?? toFixed(estimate, format.currencyDecimals);
  const change = isDecimal(received) ? max(dec(received) - estimate, 0n) : 0n;
  const paymentProblem = !takePayment
    ? null
    : !method
      ? 'Add a payment method in Settings first.'
      : !isDecimal(received) || dec(received) <= 0n
        ? 'Enter the amount received.'
        : walkIn && dec(received) < estimate
          ? `A walk-in customer pays in full: ${money(toFixed(estimate, format.currencyDecimals), format)}.`
          : null;

  const submit = () => {
    setTouched(true);
    if (problems.length || paymentProblem) return;
    setBusy(true);
    setError(null);
    onSubmit({
      docDate,
      dueDate,
      lines: qtyRowsRequest(rows),
      ...(takePayment && method
        ? { payment: { amountReceived: received, methodId: method.id } }
        : {}),
    })
      .then(onClose)
      .catch((e: unknown) => setError(errorMessage(e)))
      .finally(() => setBusy(false));
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {mode === 'deliver' ? 'Deliver goods' : 'Invoice the order'}
          <Typography color="text.secondary" variant="body2">
            {order.number} · {order.contact.name}
            {mode === 'deliver' ? ` · from ${order.location.name}` : ''}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 1, mb: 2 }}>
            <TextField
              type="date"
              label={mode === 'deliver' ? 'Delivery date' : 'Invoice date'}
              value={docDate}
              onChange={(e) => setDocDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{ width: { sm: 200 } }}
            />
            {mode === 'invoice' && (
              <TextField
                type="date"
                label="Due date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                helperText="Empty = customer’s terms"
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ width: { sm: 200 } }}
              />
            )}
            <Box sx={{ flex: 1 }} />
            <Button
              onClick={() =>
                setRows((rs) => rs.map((r) => ({ ...r, qty: allSet ? '' : r.remaining })))
              }
              sx={{ alignSelf: { sm: 'center' }, whiteSpace: 'nowrap' }}
            >
              {allSet ? 'Clear all' : `${mode === 'deliver' ? 'Deliver' : 'Invoice'} all remaining`}
            </Button>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                  Ordered
                </TableCell>
                <TableCell align="right">Left</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>
                  {mode === 'deliver' ? 'Deliver now' : 'Invoice now'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {open_.map((l, i) => {
                const row = rows[i];
                if (!row) return null;
                const over = dec(row.qty || '0') > dec(row.remaining);
                return (
                  <TableRow key={l.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {l.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {l.sku}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Qty value={l.qty} unit={l.unitSymbol} />
                    </TableCell>
                    <TableCell align="right">
                      <Qty value={row.remaining} unit={l.unitSymbol} />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        value={row.qty}
                        error={over}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => {
                          const qty = e.target.value.replace(/[^\d.]/g, '');
                          setRows((rs) => rs.map((r, j) => (j === i ? { ...r, qty } : r)));
                        }}
                        slotProps={{
                          htmlInput: {
                            inputMode: 'decimal',
                            className: 'tabular',
                            style: { textAlign: 'right' },
                            'aria-label': `${mode === 'deliver' ? 'Deliver' : 'Invoice'} quantity of ${l.description}`,
                          },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {invoicing && (
            <Box
              sx={{
                mt: 2.5,
                p: 2,
                borderRadius: 2,
                border: 1,
                borderColor: takePayment ? 'primary.main' : 'divider',
                bgcolor: takePayment ? 'action.hover' : 'transparent',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={takePayment}
                    disabled={walkIn}
                    onChange={(e) => setPaying(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 650 }}>
                      Receive payment now
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {walkIn
                        ? 'A walk-in customer pays in full when the invoice is posted.'
                        : 'Records the money against this invoice as it is posted.'}
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, alignItems: 'flex-start', gap: 1 }}
              />
              {takePayment && (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ mt: 2, alignItems: { sm: 'flex-start' } }}
                >
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={method?.id ?? null}
                    onChange={(_, v: string | null) => v && setMethodId(v)}
                    aria-label="Payment method"
                    sx={{ flexWrap: 'wrap' }}
                  >
                    {(methods.data ?? []).map((m) => (
                      <ToggleButton
                        key={m.id}
                        value={m.id}
                        sx={{ textTransform: 'none', fontWeight: 600, px: 2 }}
                      >
                        {m.name}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <TextField
                    size="small"
                    label="Amount received"
                    value={received}
                    error={touched && !!paymentProblem}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                    helperText={
                      change > 0n
                        ? `Change ${money(toFixed(change, format.currencyDecimals), format)}`
                        : `Invoice total ${money(toFixed(estimate, format.currencyDecimals), format)}`
                    }
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">{currencySymbol(format)}</InputAdornment>
                        ),
                      },
                      htmlInput: { inputMode: 'decimal', className: 'tabular' },
                    }}
                    sx={{ width: { sm: 220 } }}
                  />
                </Stack>
              )}
            </Box>
          )}
          {touched && paymentProblem && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {paymentProblem}
            </Alert>
          )}
          {touched && problems[0] && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {problems[0]}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 2 }}>
            {mode === 'deliver'
              ? 'Stock leaves at its average cost and the reservation is released.'
              : 'The invoice is posted to the customer’s account at the order’s prices.'}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={busy}>
            {mode === 'deliver' ? 'Post delivery' : 'Post invoice'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
